import { useCallback, useEffect, useState } from "react";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Petit hook utilitaire pour consommer la couche service (asynchrone).
 * Prêt pour une vraie API : si le fetch échoue, `error` est rempli et l'UI
 * peut afficher un message + un bouton "Réessayer".
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []) {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  const run = useCallback(() => {
    let alive = true;
    // Rechargement : les données précédentes restent affichées (la page n'est pas démontée, ses
    // fenêtres ouvertes non plus) jusqu'à l'arrivée des nouvelles.
    setState((prev) => ({ data: prev.data, loading: true, error: null }));
    loader()
      .then((data) => {
        if (alive) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (alive) {
          setState({
            data: null,
            loading: false,
            error: err instanceof Error ? err.message : "Une erreur est survenue.",
          });
        }
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => run(), [run]);

  return { ...state, reload: run };
}